import React from 'react';
import { SingleDatePickerShape } from 'react-dates';
import InnerDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"
import { ControlLabel, FormGroup } from 'react-bootstrap';
import moment from 'moment';
import 'react-dates/lib/css/_datepicker.css';
import './DatePicker.scss';
import { useDirtyWarningContext, DirtyWarningInputProps } from '../DirtyWarning';

declare namespace momentPropTypes {
    type momentObj = moment.Moment;
    type momentString = any;
    type momentDurationObj = any;
}

export interface DatePickerProps extends Omit<SingleDatePickerShape, "focused" | "onFocusChange" | "isOutsideRange">, DirtyWarningInputProps { 
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    readOnly?: boolean;
    block?: boolean;
    displayFormat?: string | (() => string);
    id: string;
    name?: string;
    date: momentPropTypes.momentObj | null;
    minDate?: momentPropTypes.momentObj | Date;
    maxDate?: momentPropTypes.momentObj | Date;
    minTime?: momentPropTypes.momentObj | Date;
    maxTime?: momentPropTypes.momentObj | Date;
    onDateChange: (date: momentPropTypes.momentObj | null) => void;
    customInput?: React.ReactNode;
    showTimeSelect?: boolean;
    showTimeSelectOnly?: boolean;
    timeCaption?: string;
    timeIntervals?: number;
    shouldCloseOnSelect?: boolean;
    onSelectDate?: (date: momentPropTypes.momentObj) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onEnter?: () => void;
    calendarContainer?: (props: { children: React.ReactNode[] }) => React.ReactNode;

    validationState?: () => 'success' | 'warning' | 'error' | null;
}

export const DatePicker: React.FC<DatePickerProps> = (props) => {
    const context = useDirtyWarningContext();
    const validationStateResult = props.validationState ? props.validationState() : null;
    const disableDirtyTracking = props.disableDirtyTracking || false;

    let date = (props.date == null) ? null : props.date.toDate();    
    let minDate : Date | undefined = (moment.isMoment(props.minDate)) ? (props.minDate).toDate() : props.minDate;
    let maxDate : Date | undefined = (moment.isMoment(props.maxDate)) ? (props.maxDate).toDate() : props.maxDate;

    let minTime : Date | undefined = (moment.isMoment(props.minTime)) ? (props.minTime).toDate() : props.minTime;
    let maxTime : Date | undefined = (moment.isMoment(props.maxTime)) ? (props.maxTime).toDate() : props.maxTime;

    let onChange = (d: Date | null) => {
        if (d == null) {
            props.onDateChange(null);
            return;
        }
        if (d.getFullYear() > 2100 || d.getFullYear() < 1950) return;

        let m : moment.Moment = moment(d);
        props.onDateChange(m);

        if (!disableDirtyTracking) {
            context.setDirty(true);
        }
    }

    let onSelect = (d: Date) => {
        if (d.getFullYear() > 2100 || d.getFullYear() < 1950) return;

        let m : moment.Moment = moment(d);
        if(props.onSelectDate)
            props.onSelectDate(m);
    }

    let handleChangeRaw = (value : string) => {
        if (value == null || isNaN(Number(value))) {
            onChange(null);
            return;
        }

        if (value.length == 6) {
            let mm = Number(value.substr(0, 2)) - 1;
            let dd = Number(value.substr(2, 2));
            let yy = value.substr(4, 2);
            let yyyy = Number((Number(yy) >= 70) ? "19" + yy : "20" + yy);
            let date = new Date(yyyy, mm, dd);
            onChange(date);
        }

        if (value.length == 8) {
            let mm = Number(value.substr(0, 2)) - 1;
            let dd = Number(value.substr(2, 2));
            let yyyy = Number(value.substr(4, 4));
            let date = new Date(yyyy, mm, dd);
            onChange(date);
        }
    }

    let useFormat : string | undefined = undefined;
    if (props.displayFormat) {
        if (typeof props.displayFormat === "string") {
            useFormat = props.displayFormat;
        } else {
            useFormat = props.displayFormat();
        }
        useFormat.replace("YYYY", "yyyy");
        useFormat.replace("DD", "dd");
    }

    const handleKeyDown = (event : React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && props.onEnter) {
            props.onEnter();
        }
    }

    const datePicker = <InnerDatePicker 
        selected = {date}
        onChange={onChange} 
        onChangeRaw={event => handleChangeRaw(event.target.value)}
        placeholderText={props.placeholder}
        disabled={props.disabled}
        required={props.required}
        readOnly={props.readOnly}
        dateFormat={useFormat}
        id={props.id}
        name={props.name}
        minDate={minDate}
        maxDate={maxDate}
        minTime={minTime}
        maxTime={maxTime}
        className="form-control"
        wrapperClassName={(props.block) ? "block" : ""}
        customInput={props.customInput}
        showTimeSelect={props.showTimeSelect}
        showTimeSelectOnly={props.showTimeSelectOnly}
        timeCaption={props.timeCaption}
        timeIntervals={props.timeIntervals}
        shouldCloseOnSelect={props.shouldCloseOnSelect}
        calendarContainer={props.calendarContainer}
        onSelect={onSelect} 
        onBlur={props.onBlur}
        onKeyDown={handleKeyDown}>
            {props.children}
        </InnerDatePicker>

    if (props.label == null) {
        return datePicker;
    }

    return (
        <FormGroup validationState={validationStateResult}>
            <ControlLabel>{props.label}</ControlLabel>
            <div>
                {datePicker}
            </div>
        </FormGroup>
    )
}

(DatePicker as any).displayName = "TsDatePicker" //for react dev tools


/*

Here is the minimum REQUIRED setup you need to get the DatePicker working:

<SingleDatePicker
  date={this.state.date} // momentPropTypes.momentObj or null
  onDateChange={date => this.setState({ date })} // PropTypes.func.isRequired
  id="your_unique_id" // PropTypes.string.isRequired,
/>

*/